/**
 * Mobile mega-menu bridge.
 *
 * Pairs each <div class="mega-menu-mobile-panel" data-mobile-mega-menu-handle="...">
 * (rendered hidden inside <header-menu>) with the drawer's top-level
 * <li data-link-handle="..."> and rewrites that item into the drawer's native
 * drill-in <details> pattern. The mobile panel itself is purpose-built for the
 * drawer width — this script just portals its content into the drill-in and lets
 * the drawer's existing open/back/close actions handle the animation.
 *
 * Each li tracks a content signature of its source panel; we only rebuild when
 * that signature changes (e.g., merchant edits in the theme editor), which also
 * means the drawer-subtree observer can't trigger runaway re-syncs from our own
 * injections.
 */

const PROCESSED_ATTR = 'data-mobile-mega-processed';
const SIG_PROP = '_mobileMegaSig';
const HANDLE_ATTR = 'data-link-handle';
const SOURCE_HANDLE_ATTR = 'data-mobile-mega-menu-handle';

/* The drawer's .icon-caret--forward / --backward classes apply transform: rotate(±90deg)
   on the assumption the source SVG is a down-pointing chevron (see assets/icon-caret.svg
   and the rules in base.css). These inline SVGs match that base orientation so the
   existing rotation classes produce: forward = right, backward = left. */
/* Inline SVGs mirror the markup of assets/icon-caret.svg and assets/icon-close.svg
   so they pick up the theme's --icon-stroke-width setting and respond to the
   existing icon-caret rotation classes. */
const ICON_CARET_DOWN =
  '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_CLOSE =
  '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const normalize = (v) => (v || '').trim().toLowerCase();

function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );
}

function getSourcePanels() {
  return document.querySelectorAll(`[${SOURCE_HANDLE_ATTR}]`);
}

/* Editor instrumentation attached by `{{ block.shopify_attributes }}` (only present
   in design mode). Cloning the source panel into the drawer would otherwise leave
   the same editor block IDs in two DOM locations, which the theme editor tries to
   reconcile — generating its own mutations that feed back into our observer. */
function stripShopifyEditorAttrs(root) {
  const strip = (el) => {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('data-shopify')) el.removeAttribute(attr.name);
    }
  };
  strip(root);
  root.querySelectorAll('*').forEach(strip);
}

function getDrawerMenuRoot() {
  return document.querySelector('header-drawer .menu-drawer__menu');
}

function findDrawerListItem(handle) {
  const root = getDrawerMenuRoot();
  if (!root) return null;
  return root.querySelector(`:scope > li[${HANDLE_ATTR}="${CSS.escape(handle)}"]`);
}

/**
 * Wraps each .mega-menu-column with a heading into a <details> accordion.
 * Columns without a heading stay flat — there'd be nothing meaningful to put
 * in the summary.
 *
 * Structure before:
 *   <div class="mega-menu-column">
 *     <p class="mega-menu-column__heading">Heading</p>
 *     <ul class="mega-menu-column__list">…</ul>
 *     <a class="mega-menu-column__view-all">…</a>?
 *   </div>
 *
 * After:
 *   <div class="mega-menu-column mega-menu-column--accordion">
 *     <details class="mega-menu-column__accordion">
 *       <summary class="mega-menu-column__heading">Heading <chevron/></summary>
 *       <div class="mega-menu-column__accordion-body">
 *         <ul>…</ul><a>…</a>
 *       </div>
 *     </details>
 *   </div>
 */
function convertColumnsToAccordions(panelClone) {
  panelClone.querySelectorAll('.mega-menu-column').forEach((column) => {
    const heading = column.querySelector(':scope > .mega-menu-column__heading');
    if (!heading) return;

    const summary = document.createElement('summary');
    summary.className = heading.className;
    summary.innerHTML = `
      <span>${heading.innerHTML}</span>
      <span class="mega-menu-column__accordion-icon" aria-hidden="true">${ICON_CARET_DOWN}</span>
    `;

    const body = document.createElement('div');
    body.className = 'mega-menu-column__accordion-body';
    let sibling = heading.nextSibling;
    while (sibling) {
      const next = sibling.nextSibling;
      body.appendChild(sibling);
      sibling = next;
    }

    const details = document.createElement('details');
    details.className = 'mega-menu-column__accordion';
    details.appendChild(summary);
    details.appendChild(body);

    heading.remove();
    column.appendChild(details);
    column.classList.add('mega-menu-column--accordion');
  });
}

function buildDrillIn(li, sourcePanel, handle) {
  const existingLink = li.querySelector(':scope > a.menu-drawer__menu-item--mainlist');
  if (!existingLink) return;

  const title =
    existingLink.querySelector('.menu-drawer__menu-item-text')?.textContent?.trim() || handle;
  const href = existingLink.getAttribute('href') || '#';
  /* Split row: when the top-level nav item points at a real page, the title is a
     link to that page and only the chevron zone opens the drill-in. Most Frelan
     top-level items are '#' mega-menu triggers — those keep the full-row toggle.
     The optional "Shop all" parent-link row only makes sense alongside a real
     page too, so it shares the same guard. */
  const hasPageLink = href !== '#' && href !== '';
  const showParentLink = sourcePanel.dataset.showParentLink === 'true' && hasPageLink;
  const parentLabel =
    sourcePanel.dataset.parentLinkLabel?.trim() || `Shop all ${title}`;

  const details = document.createElement('details');
  details.id = `Details-menu-drawer-mobile-mega-${handle}`;
  details.className = 'menu-drawer__menu-container menu-drawer__menu-container--mega';

  const summary = document.createElement('summary');
  summary.id = `HeaderDrawer-mobile-mega-${handle}`;
  summary.className =
    'menu-drawer__menu-item menu-drawer__menu-item--mainlist menu-drawer__animated-element focus-inset';
  summary.setAttribute('on:click', 'header-drawer/open');
  summary.classList.add('menu-drawer__menu-item--split');
  summary.innerHTML = `
    ${
      hasPageLink
        ? `<a href="${escapeHtml(href)}" class="menu-drawer__menu-item-text wrap-text menu-drawer__split-link">${escapeHtml(title)}</a>`
        : `<span class="menu-drawer__menu-item-text wrap-text">${escapeHtml(title)}</span>`
    }
    <span class="svg-wrapper icon-caret icon-caret--forward menu-drawer__split-toggle">${ICON_CARET_DOWN}</span>
  `;

  /* An interactive <a> inside <summary> navigates instead of toggling the
     details, but the drawer's delegated on:click open handler on the summary
     would still fire via bubbling — stop it so tapping the title only navigates. */
  const splitLink = summary.querySelector('.menu-drawer__split-link');
  if (splitLink) splitLink.addEventListener('click', (event) => event.stopPropagation());

  const submenu = document.createElement('div');
  submenu.id = `link-mobile-mega-${handle}`;
  submenu.className = 'menu-drawer__submenu menu-drawer__submenu--mega has-submenu gradient motion-reduce';
  submenu.tabIndex = -1;

  const inner = document.createElement('div');
  inner.className = 'menu-drawer__inner-submenu';

  const navButtons = document.createElement('div');
  navButtons.className = 'menu-drawer__nav-buttons';
  navButtons.innerHTML = `
    <button class="button menu-drawer__back-button focus-inset" type="button" aria-expanded="true" aria-label="Back" on:click="header-drawer/back">
      <span class="svg-wrapper icon-caret icon-caret--backward">${ICON_CARET_DOWN}</span>
      <span class="menu-drawer__menu-item-text wrap-text">${escapeHtml(title)}</span>
    </button>
    <button class="button button-unstyled close-button menu-drawer__close-button" type="button" aria-label="Close" on:click="header-drawer/close">
      <span class="svg-wrapper header-drawer-icon header-drawer-icon--close">${ICON_CLOSE}</span>
    </button>
  `;
  inner.appendChild(navButtons);

  if (showParentLink) {
    const parentLink = document.createElement('a');
    parentLink.href = href;
    parentLink.className = 'mega-menu-mobile-panel__parent-link menu-drawer__animated-element focus-inset';
    parentLink.innerHTML = `
      <span class="menu-drawer__menu-item-text wrap-text">${escapeHtml(parentLabel)}</span>
      <span class="mega-menu-mobile-panel__parent-link-arrow" aria-hidden="true">&rarr;</span>
    `;
    inner.appendChild(parentLink);
  }

  const panelClone = sourcePanel.cloneNode(true);
  panelClone.removeAttribute('hidden');
  panelClone.removeAttribute('id');
  panelClone.classList.add('mega-menu-mobile-panel--in-drawer');
  panelClone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  stripShopifyEditorAttrs(panelClone);

  if (sourcePanel.dataset.collapseColumns === 'true') {
    convertColumnsToAccordions(panelClone);
  }

  inner.appendChild(panelClone);

  submenu.appendChild(inner);
  details.appendChild(summary);
  details.appendChild(submenu);

  existingLink.replaceWith(details);
  li.setAttribute(PROCESSED_ATTR, handle);
  li.classList.add('menu-drawer__list-item--mega');
}

function teardownDrillIn(li) {
  const details = li.querySelector(':scope > details.menu-drawer__menu-container--mega');
  if (!details) return;

  const title =
    details.querySelector('summary .menu-drawer__menu-item-text')?.textContent?.trim() || '';
  const splitLink = details.querySelector('summary a.menu-drawer__split-link');
  const parentLink = details.querySelector('.mega-menu-mobile-panel__parent-link');
  const href = splitLink?.getAttribute('href') || parentLink?.getAttribute('href') || '#';

  const a = document.createElement('a');
  a.className =
    'menu-drawer__menu-item menu-drawer__menu-item--mainlist menu-drawer__animated-element focus-inset';
  a.href = href;
  a.innerHTML = `<span class="menu-drawer__menu-item-text wrap-text">${escapeHtml(title)}</span>`;

  details.replaceWith(a);
  li.removeAttribute(PROCESSED_ATTR);
  li.classList.remove('menu-drawer__list-item--mega');
  delete li[SIG_PROP];
}

let syncing = false;

function sync() {
  if (syncing) return;
  syncing = true;
  try {
    const drawerRoot = getDrawerMenuRoot();
    if (!drawerRoot) return;

    const panels = getSourcePanels();
    const handlesSeen = new Set();

    panels.forEach((panel) => {
      const handle = normalize(panel.getAttribute(SOURCE_HANDLE_ATTR));
      if (!handle) return;
      handlesSeen.add(handle);

      const li = findDrawerListItem(handle);
      if (!li) return;

      /* Sig deliberately excludes panel.innerHTML. In design mode the editor
         continually decorates nested blocks (block.shopify_attributes are
         added/updated as the editor hydrates), which flips innerHTML on every
         mutation and used to trigger a teardown+rebuild loop that left the
         drawer in a permanent half-built state. Merchant-driven content
         changes still rebuild via the shopify:section:load handler below,
         which invalidates the cached sig.
         The drawerHasDetails check covers the live storefront case: section-
         hydration runs once at idle and morphs the header section, replacing
         our injected <details> with the server-rendered <a>. Without this
         check, the dataset-only sig still matches and sync skips, leaving the
         drawer stuck on <a>. */
      const sig = `${handle}|${panel.dataset.showParentLink}|${panel.dataset.parentLinkLabel || ''}|${panel.dataset.collapseColumns}`;
      const drawerHasDetails = li.querySelector(':scope > details.menu-drawer__menu-container--mega') !== null;
      if (li[SIG_PROP] === sig && drawerHasDetails) return;

      if (li.getAttribute(PROCESSED_ATTR)) teardownDrillIn(li);
      buildDrillIn(li, panel, handle);
      li[SIG_PROP] = sig;
    });

    drawerRoot.querySelectorAll(`:scope > li[${PROCESSED_ATTR}]`).forEach((li) => {
      const stored = normalize(li.getAttribute(PROCESSED_ATTR));
      if (!handlesSeen.has(stored)) teardownDrillIn(li);
    });
  } finally {
    syncing = false;
  }
}

/* Debounced (not microtask) so the editor's hydration burst — dozens of nested
   block mutations across separate tasks — coalesces into one sync rather than
   triggering a full rebuild per mutation. Microtask scheduling was tight enough
   to fire between each editor task, which locked up the editor on load. */
let scheduledTimer = null;
function scheduleSync() {
  if (scheduledTimer != null) return;
  scheduledTimer = setTimeout(() => {
    scheduledTimer = null;
    sync();
  }, 100);
}

const observers = new WeakMap();
function observeIfNeeded(el) {
  if (!el || observers.has(el)) return;
  const obs = new MutationObserver(scheduleSync);
  obs.observe(el, { childList: true, subtree: true });
  observers.set(el, obs);
}

function start() {
  scheduleSync();
  observeIfNeeded(document.querySelector('header-menu'));
  observeIfNeeded(document.querySelector('header-drawer'));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}

/* On merchant edits (these events fire when settings actually change), clear
   cached sigs so the next sync rebuilds with the new content. Without this,
   the dataset-only sig wouldn't notice nested block changes. */
function invalidateAndStart() {
  const drawerRoot = getDrawerMenuRoot();
  if (drawerRoot) {
    drawerRoot.querySelectorAll(`:scope > li[${PROCESSED_ATTR}]`).forEach((li) => {
      delete li[SIG_PROP];
    });
  }
  start();
}

document.addEventListener('shopify:section:load', invalidateAndStart);
document.addEventListener('shopify:section:unload', invalidateAndStart);
document.addEventListener('shopify:block:select', invalidateAndStart);
document.addEventListener('shopify:block:deselect', invalidateAndStart);
